import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Delete,
  Edit,
  Add as AddIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useSnackbar } from "../../context/SnackbarContext";
import { useCompany } from "../../context/CompanyContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openScanModal, setOpenScanModal] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanningCompanyId, setScanningCompanyId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newCompanyData, setNewCompanyData] = useState({
    name: "",
    company_code: "",
    inbox_email: "",
    invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
    invoice_sender_whitelist: "",
    invoice_email_pdf_split_enabled: false,
    description: "",
  });
  const { setSelectedCompany: setContextSelectedCompany, selectedCompany: contextSelectedCompany } =
    useCompany();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();

  const fetchCompanies = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/companies/`);
      if (response.data.companies) {
        setCompanies(response.data.companies);
      }
    } catch (error) {
      showSnackbar("Error fetching companies", "error");
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSelectCompany = async (company) => {
    setContextSelectedCompany(company);
    showSnackbar(`Selected company: ${company.name}`, "success");
  };

  const handleDeleteCompany = async () => {
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/company/${selectedCompany.id}/delete/`);
      fetchCompanies();
      showSnackbar("Company deleted successfully", "success");
      setOpenDeleteModal(false);
    } catch (error) {
      showSnackbar("Error deleting company", "error");
    }
  };

  const handleAddCompany = async () => {
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/company/create/`, newCompanyData);
      fetchCompanies();
      showSnackbar("Company added successfully", "success");
      setOpenAddModal(false);
      setNewCompanyData({
        name: "",
        company_code: "",
        inbox_email: "",
        invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
        invoice_sender_whitelist: "",
        invoice_email_pdf_split_enabled: false,
        description: "",
      });
    } catch (error) {
      showSnackbar("Error adding company", "error");
    }
  };

  const handleEditCompany = async () => {
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/api/company/${selectedCompany.id}/update/`, {
        name: newCompanyData.name,
        company_code: newCompanyData.company_code,
        inbox_email: newCompanyData.inbox_email,
        invoice_allowed_extensions: newCompanyData.invoice_allowed_extensions,
        invoice_sender_whitelist: newCompanyData.invoice_sender_whitelist,
        invoice_email_pdf_split_enabled: newCompanyData.invoice_email_pdf_split_enabled,
        description: newCompanyData.description,
      });
      fetchCompanies();
      showSnackbar("Company updated successfully", "success");
      setOpenEditModal(false);
    } catch (error) {
      showSnackbar("Error updating company", "error");
    }
  };

  const handleOpenAddModal = () => {
    setNewCompanyData({
      name: "",
      company_code: "",
      inbox_email: "",
      invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
      invoice_sender_whitelist: "",
      description: "",
    });
    setOpenAddModal(true);
  };

  const emailStatusLabel = (status) =>
    ({
      imported: t("emailStatusImported"),
      pending_split: t("emailStatusPendingSplit"),
      skipped: t("emailStatusSkipped"),
      failed: t("emailStatusFailed"),
      already_processed: t("emailStatusAlreadyProcessed"),
    }[status] ||
    status ||
    "-");

  const documentStatusLabel = (status) =>
    ({
      present: t("documentStatusPresent"),
      partial: t("documentStatusPartial"),
      missing: t("documentStatusMissing"),
      unknown: t("documentStatusUnknown"),
      none: t("documentStatusNone"),
    }[status] ||
    status ||
    "-");

  const handleScanInbox = async (company) => {
    setScanningCompanyId(company.id);
    setScanResult(null);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/api/company/${company.id}/scan-invoice-email/`,
        { limit: 100 }
      );
      setScanResult(response.data);
      setOpenScanModal(true);
      showSnackbar(t("invoiceEmailScanCompleted"), "success");
      fetchCompanies();
    } catch (error) {
      const message = error.response?.data?.error || t("invoiceEmailScanFailed");
      showSnackbar(message, "error");
    } finally {
      setScanningCompanyId(null);
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" color="white">
                    {t("companyManagement")}
                  </MDTypography>
                  <MDButton
                    variant="contained"
                    color="white"
                    onClick={handleOpenAddModal}
                    startIcon={<AddIcon />}
                    sx={{
                      boxShadow: 1,
                      "&:hover": {
                        boxShadow: 2,
                      },
                    }}
                  >
                    {t("addCompany")}
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#f8f9fa" }}>
                          <th style={{ padding: "16px", textAlign: "left" }}>{t("companyName")}</th>
                          <th style={{ padding: "16px", textAlign: "left" }}>{t("companyCode")}</th>
                          <th style={{ padding: "16px", textAlign: "left" }}>
                            {t("invoiceEmail")}
                          </th>
                          <th style={{ padding: "16px", textAlign: "left" }}>{t("description")}</th>
                          <th style={{ padding: "16px", textAlign: "center" }}>{t("status")}</th>
                          <th style={{ padding: "16px", textAlign: "center" }}>{t("actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.map((company) => (
                          <tr key={company.id}>
                            <td style={{ padding: "16px" }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <BusinessIcon color="info" />
                                <Typography variant="body2">{company.name}</Typography>
                              </Box>
                            </td>
                            <td style={{ padding: "16px" }}>
                              <Typography variant="body2">{company.company_code}</Typography>
                            </td>
                            <td style={{ padding: "16px" }}>
                              <Typography variant="body2" color="text.secondary">
                                {company.inbox_email || "-"}
                              </Typography>
                            </td>
                            <td style={{ padding: "16px" }}>
                              <Typography variant="body2" color="text.secondary">
                                {company.description || "-"}
                              </Typography>
                            </td>
                            <td style={{ padding: "16px", textAlign: "center" }}>
                              {contextSelectedCompany?.id === company.id ? (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label={t("selected")}
                                  color="success"
                                  size="small"
                                  sx={{ minWidth: 100 }}
                                />
                              ) : (
                                <Chip
                                  label={t("notSelected")}
                                  color="default"
                                  size="small"
                                  sx={{ minWidth: 100 }}
                                />
                              )}
                            </td>
                            <td style={{ padding: "16px", textAlign: "center" }}>
                              <Box display="flex" justifyContent="center" gap={1}>
                                <Box
                                  sx={{
                                    minWidth: 120,
                                    visibility:
                                      contextSelectedCompany?.id === company.id
                                        ? "hidden"
                                        : "visible",
                                  }}
                                >
                                  <MDButton
                                    onClick={() => handleSelectCompany(company)}
                                    variant="contained"
                                    color="info"
                                    size="small"
                                    fullWidth
                                  >
                                    {t("select")}
                                  </MDButton>
                                </Box>
                                <Tooltip title={t("scanInvoiceEmail")}>
                                  <span>
                                    <IconButton
                                      onClick={() => handleScanInbox(company)}
                                      color="info"
                                      size="small"
                                      disabled={scanningCompanyId === company.id}
                                    >
                                      <EmailIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={t("editCompany")}>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedCompany(company);
                                      setNewCompanyData({
                                        name: company.name,
                                        company_code: company.company_code,
                                        inbox_email: company.inbox_email || "",
                                        invoice_allowed_extensions:
                                          company.invoice_allowed_extensions ||
                                          ".pdf,.png,.jpg,.jpeg",
                                        invoice_sender_whitelist:
                                          company.invoice_sender_whitelist || "",
                                        invoice_email_pdf_split_enabled:
                                          !!company.invoice_email_pdf_split_enabled,
                                        description: company.description || "",
                                      });
                                      setOpenEditModal(true);
                                    }}
                                    color="primary"
                                    size="small"
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("deleteCompany")}>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedCompany(company);
                                      setOpenDeleteModal(true);
                                    }}
                                    color="error"
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Add Company Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("addNewCompany")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              autoFocus
              label={t("companyName")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.name}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("companyCode")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.company_code}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, company_code: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("invoiceEmail")}
              type="email"
              fullWidth
              variant="outlined"
              value={newCompanyData.inbox_email}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, inbox_email: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("invoiceAllowedExtensions")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.invoice_allowed_extensions}
              onChange={(e) =>
                setNewCompanyData({
                  ...newCompanyData,
                  invoice_allowed_extensions: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("invoiceSenderWhitelist")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.invoice_sender_whitelist}
              onChange={(e) =>
                setNewCompanyData({
                  ...newCompanyData,
                  invoice_sender_whitelist: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!newCompanyData.invoice_email_pdf_split_enabled}
                  onChange={(e) =>
                    setNewCompanyData({
                      ...newCompanyData,
                      invoice_email_pdf_split_enabled: e.target.checked,
                    })
                  }
                />
              }
              label={t("invoiceEmailPdfSplitEnabled")}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("description")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.description}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, description: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenAddModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleAddCompany}>
            {t("addCompany")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Edit Company Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("editCompany")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              autoFocus
              label={t("companyName")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.name}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("companyCode")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.company_code}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, company_code: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("invoiceEmail")}
              type="email"
              fullWidth
              variant="outlined"
              value={newCompanyData.inbox_email}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, inbox_email: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("invoiceAllowedExtensions")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.invoice_allowed_extensions}
              onChange={(e) =>
                setNewCompanyData({
                  ...newCompanyData,
                  invoice_allowed_extensions: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("invoiceSenderWhitelist")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.invoice_sender_whitelist}
              onChange={(e) =>
                setNewCompanyData({
                  ...newCompanyData,
                  invoice_sender_whitelist: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!newCompanyData.invoice_email_pdf_split_enabled}
                  onChange={(e) =>
                    setNewCompanyData({
                      ...newCompanyData,
                      invoice_email_pdf_split_enabled: e.target.checked,
                    })
                  }
                />
              }
              label={t("invoiceEmailPdfSplitEnabled")}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("description")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.description}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, description: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenEditModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleEditCompany}>
            {t("saveChanges")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Invoice Email Scan Result Modal */}
      <Dialog open={openScanModal} onClose={() => setOpenScanModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("invoiceEmailScanResult")}</DialogTitle>
        <DialogContent>
          {scanResult && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("foundMessages")}
                  </Typography>
                  <Typography variant="h6">{scanResult.found_messages}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("importedDocuments")}
                  </Typography>
                  <Typography variant="h6">{scanResult.imported_documents}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("skippedMessages")}
                  </Typography>
                  <Typography variant="h6">{scanResult.skipped_messages}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("alreadyProcessed")}
                  </Typography>
                  <Typography variant="h6">{scanResult.already_processed_messages}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("pendingSplitMessages")}
                  </Typography>
                  <Typography variant="h6">{scanResult.pending_split_messages || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("pendingSplitJobs")}
                  </Typography>
                  <Typography variant="h6">{scanResult.pending_split_jobs || 0}</Typography>
                </Grid>
              </Grid>
              <Divider />
              <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                {(scanResult.messages || []).map((message) => {
                  const documentsStatus = message.documents_status || {};
                  const documentsStatusText = `${documentsStatus.existing_count || 0}/${
                    documentsStatus.expected_count || message.imported_documents_count || 0
                  }`;
                  return (
                    <Accordion key={message.uid} disableGutters variant="outlined" sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                          flexWrap="wrap"
                          width="100%"
                        >
                          <Box minWidth={180}>
                            <Typography variant="body2" fontWeight="medium">
                              {message.message_date
                                ? new Date(message.message_date).toLocaleString()
                                : t("dateUnknown")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              UID {message.uid}
                            </Typography>
                          </Box>
                          <Box flex={1} minWidth={220}>
                            <Typography variant="body2">{message.sender || "-"}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {message.subject || "-"}
                            </Typography>
                          </Box>
                          <Chip size="small" label={emailStatusLabel(message.status)} />
                          <Chip
                            size="small"
                            color={documentsStatus.status === "present" ? "success" : "default"}
                            label={`${t("documents")}: ${documentsStatusText}`}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              {t("company")}
                            </Typography>
                            <Typography variant="body2">{message.company || "-"}</Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              {t("processedAt")}
                            </Typography>
                            <Typography variant="body2">
                              {message.processed_at
                                ? new Date(message.processed_at).toLocaleString()
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t("attachments")}
                            </Typography>
                            {(message.attachment_names || []).length > 0 ? (
                              <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                                {message.attachment_names.map((name) => (
                                  <Chip key={`${message.uid}-${name}`} size="small" label={name} />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2">{t("notAvailableForOldLog")}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t("documentCheck")}
                            </Typography>
                            <Typography variant="body2">
                              {documentStatusLabel(documentsStatus.status)}: {documentsStatusText}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t("recipients")}
                            </Typography>
                            <Typography variant="body2">{message.recipients || "-"}</Typography>
                          </Grid>
                        </Grid>
                        {message.error && (
                          <Typography variant="caption" color="error" display="block" mt={1}>
                            {message.error}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="contained" color="info" onClick={() => setOpenScanModal(false)}>
            {t("close")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("areYouSure")} {selectedCompany?.name}? {t("thisActionCannotBeUndone")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenDeleteModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="error" onClick={handleDeleteCompany}>
            {t("delete")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default CompanyManagement;
